import React from 'react';
import styled, { css } from 'styled-components';
import { tracker } from 'src/utils/analytics';
import { AddToAppleWallet } from 'src/assets/loyalty-pass/add-to-apple-wallet';
import { AppleWalletIcon } from 'src/assets/loyalty-pass/apple-wallet-icon';
import { ButtonWrapper } from './footer.styles';
import { useLoyaltyPassImpressionTracking } from '../use-loyalty-pass-impressions';

type AppleWalletDesktopButtonProps = {
  onClick?: (e: React.MouseEvent) => void;
  preventEventBubbling?: boolean;
};

type AppleWalletMobileButtonProps = AppleWalletDesktopButtonProps & {
  href: string;
};

/**
 * Desktop button that opens QR code modal for Apple Wallet pass
 */
export const AppleWalletDesktopButton: React.FC<AppleWalletDesktopButtonProps> = ({
  onClick,
  preventEventBubbling = false,
}) => {
  const { ref } = useLoyaltyPassImpressionTracking({ type: 'button' });
  const handleClick = (e: React.MouseEvent): void => {
    if (preventEventBubbling) {
      e.stopPropagation();
      e.preventDefault();
    }
    tracker.walletPass({ action: 'clicked', type: 'button' });
    onClick?.(e);
  };
  return (
    <ButtonWrapper ref={ref}>
      <DesktopWalletButton onClick={handleClick} aria-label='Add to Apple Wallet' type='button'>
        <AddToAppleWallet />
      </DesktopWalletButton>
    </ButtonWrapper>
  );
};

/**
 * Mobile button that navigates directly to Apple Wallet pass URL.
 * Uses button element to avoid nested anchor tags when inside link wrappers.
 */
export const AppleWalletMobileButton: React.FC<AppleWalletMobileButtonProps> = ({
  href,
  onClick,
  preventEventBubbling = false,
}) => {
  const { ref } = useLoyaltyPassImpressionTracking({ type: 'button' });
  const handleClick = (e: React.MouseEvent): void => {
    if (preventEventBubbling) {
      e.stopPropagation();
      e.preventDefault();
    }
    tracker.walletPass({ action: 'clicked', type: 'button' });
    onClick?.(e);
    // Navigate to the pass URL for direct download
    window.location.href = href;
  };

  return (
    <MobileWalletButton onClick={handleClick} aria-label='Add to Apple Wallet' type='button' ref={ref}>
      <AppleWalletIcon width={20} height={16} aria-hidden='true' />
      <span>
        Add to <b>Apple Wallet</b>
      </span>
    </MobileWalletButton>
  );
};

export const SharedWalletButtonStyles = css`
  border: none;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }
`;

export const DesktopWalletButton = styled.button`
  ${SharedWalletButtonStyles}
  padding: 0;
  display: flex;
  align-items: center;
  background: transparent;

  svg {
    width: auto;
    height: 36px;
  }
`;

export const MobileWalletButton = styled.button`
  ${SharedWalletButtonStyles}
  ${({ theme: { colors, spaces, customized } }) => css`
    width: 100%;
    background: ${colors.primaryBlack};
    border-radius: ${customized.radius.md};
    min-height: 36px;
    padding: ${spaces[2]} ${spaces[3]};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${spaces[2]};
    font-size: 14px;
    color: ${colors.white};
  `}
`;
